import { useCallback } from 'react';
import { useAuth } from '../../../../../auth/context/native/AuthContext';
import type { OnActionType } from '../../../../context/ActionsProvider/context';
import { SHOULD_NOT_PROPAGATE_UP } from '../../const';
import { useTrackLiveLocation } from '../controlLiveLocation';
import { useLogout } from '../logout';
import { useRefreshUserContext } from '../refreshUserContext';
import { useSetColorScheme } from '../setColorScheme';
import { useSetLanguage } from '../setLanguage';
import { useSetTheme } from '../setTheme';
import { useSetUserPreference } from '../setUserPreference';
import { useSignInWithSSO } from '../signInWithSSO';
import { useUpdateGeolocation } from '../updateGeolocation';
import { UserControlAction } from './schema/controlUser';

function useControlUser() {
  const { onSetUserPreference } = useSetUserPreference();
  const { updateGeolocation } = useUpdateGeolocation();
  const { onSetLanguage } = useSetLanguage();
  const { onSetColorScheme } = useSetColorScheme();
  const { onSetTheme } = useSetTheme();
  const { onLogout } = useLogout();
  const { checkAuth } = useAuth();
  const { onRefreshUserContext } = useRefreshUserContext();
  const { signInWithSSO } = useSignInWithSSO();
  const { onTrackLiveLocation } = useTrackLiveLocation();

  const onControlUser: OnActionType = useCallback(
    async (args) => {
      const { action } = args;
      const userControlAction = action.payload?.userControlAction;

      switch (userControlAction) {
        case UserControlAction.SetUserPreference:
          return onSetUserPreference(args);
        case UserControlAction.Logout:
          return onLogout();
        case UserControlAction.UpdateGeolocation:
          return updateGeolocation(args);
        case UserControlAction.SetLanguage:
          return onSetLanguage(args);
        case UserControlAction.SetColorScheme:
          return onSetColorScheme(args);
        case UserControlAction.SetInterfaceTheme:
          return onSetTheme(args);
        case UserControlAction.CheckAuth: {
          await checkAuth();
          return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
        }
        case UserControlAction.RefreshUserContext:
          return onRefreshUserContext();
        case UserControlAction.SignInWithSSO: {
          await signInWithSSO(args);
          return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
        }
        case UserControlAction.TrackLiveLocation:
          return onTrackLiveLocation(args);
        default:
          return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
      }
    },
    [
      onSetUserPreference,
      onLogout,
      updateGeolocation,
      onSetLanguage,
      onSetColorScheme,
      onSetTheme,
      checkAuth,
      onRefreshUserContext,
      signInWithSSO,
      onTrackLiveLocation,
    ],
  );

  return { onControlUser };
}

export default useControlUser;
